import React from 'react';

export interface TheorySection {
    id: string;
    title: string;
    content: React.ReactNode;
}

export const theorySections: TheorySection[] = [
    {
        id: 'patterns',
        title: '24 Patterns (Tul)',
        content: (
            <>
                <p>
                    There are 24 Patterns in Taekwon-Do. There are nine colour belt patterns, then three each for 1st, 2nd, 3rd, 4th degree, 2 for 5th degree and one for 6th degree.
                </p>
                <br />
                <p>
                    General Choi Hong Hi gave the following explanation for his choice of 24 patterns:
                </p>
                <br />
                <blockquote className="theory-quote">
                    <p>
                        "The life span of a human, perhaps 100 years, can be considered as merely a day when compared with eternity. We mortals, therefore, are no more than simple travellers who pass by the eternal years of an aeon in a day.
                    </p>
                    <br />
                    <p>
                        No one can live more than a limited time; nevertheless, many people foolishly enslave themselves to materialism as if they could live for thousands of years. Others, however, strive to bequeath a good spiritual legacy for coming generations, in this way gaining immortality. What we can do to leave behind something for the welfare of mankind is, perhaps, the most important thing in our lives.
                    </p>
                    <br />
                    <p>
                        Here I leave Taekwon-Do for mankind as a trace of man of the late 20th century. The 24 patterns represent 24 hours &ndash; one day, or all my life."
                    </p>
                </blockquote>
            </>
        ),
    },
    {
        id: 'itf-dobok',
        title: 'The ITF Dobok',
        content: (
            <>
                <p>
                    The training suit worn in class is known as the "Dobok" in Korean (literally "way clothing") and is white in colour. Grade holders (colour belts) wear the basic Dobok. The shirt has the ITF badge on the left chest, and the "Taekwon-Do Tree" on the back. The trousers have the letters "ITF" on the outside of each leg at knee level.
                </p>
                <br />
                <p>
                    1st to 3rd degree Black Belts wear an additional black piping around the bottom edge of the shirt, below the belt.
                </p>
                <br />
                <p>
                    4th degree Black Belts and above wear additional black stripes down the outsides of the trouser legs.
                </p>
                <br />
                <p>
                    4th degree Black Belts and above who are also qualified International Instructors, wear additional black stripes down the outsides of the sleeves.
                </p>
                <br />
                <p>
                    7th degree Black Belts and above have an additional white stripe added down the centre of the black piping on the arms and legs to differentiate a Grandmaster or Master from an International Instructor.
                </p>
                <br />
                <p>
                    In addition, all Black Belts may wear shoulder epaulettes, signifying their title (Assistant Instructor, Instructor, Master, Grandmaster).
                </p>
            </>
        ),
    },
    {
        id: 'significance-of-one',
        title: 'The Significance of "One"',
        content: (
            <>
                <p>
                    The Taekwon-Do belt is tied around the waist only once to signify:
                </p>
                <ul className="theory-list">
                    <li>Pursue ONE goal (Oh-do-il-kwan)</li>
                    <li>Serve ONE Master with unshakable loyalty (il-pyon-dan-shim)</li>
                    <li>Achieve victory in ONE blow (Il-kyok-pil-sung)</li>
                </ul>
            </>
        ),
    },
    {
        id: 'attacking-and-blocking-tools',
        title: 'Attacking and Blocking Tools',
        content: (
            <>
                <p>
                    Taekwon-Do uses a wide variety of attacking and blocking tools &mdash; the specific parts of the hand, arm, foot and leg used to strike or block. Each tool is chosen for the technique it performs best.
                </p>
                <br />
                <figure className="theory-figure">
                    <img
                        src="/images/theory/attacking-blocking-tools.jpg"
                        alt="Diagram showing attacking and blocking tools: outer forearm, inner forearm, side fist, back fist, reverse knifehand, knifehand, palm, fingertip, toes, side sole, forefist, instep, side instep, foot sword, back heel, ball of the foot, back sole"
                    />
                    <figcaption>Attacking and blocking tools of the hand, arm and foot.</figcaption>
                </figure>
            </>
        ),
    },
    {
        id: 'sections-of-the-body',
        title: 'Sections of the Body',
        content: (
            <>
                <p>
                    Taekwon-Do techniques are directed at one of three horizontal sections of the body, and are aligned vertically relative to three reference lines.
                </p>
                <br />
                <p>Vertical reference lines:</p>
                <ul className="theory-list">
                    <li><strong>A</strong> &ndash; Centre line</li>
                    <li><strong>B</strong> &ndash; Chest line</li>
                    <li><strong>C</strong> &ndash; Shoulder line</li>
                </ul>
                <br />
                <p>Horizontal sections:</p>
                <ul className="theory-list">
                    <li><strong>High section</strong> &ndash; above the shoulders (the head and neck)</li>
                    <li><strong>Middle section</strong> &ndash; between the shoulders and the belt (the torso)</li>
                    <li><strong>Low section</strong> &ndash; below the belt</li>
                </ul>
                <br />
                <figure className="theory-figure">
                    <img
                        src="/images/theory/sections-of-the-body.jpg"
                        alt="Diagram showing the three sections of the body (high, middle, low) and the three vertical reference lines: centre line, chest line and shoulder line"
                    />
                    <figcaption>Sections of the body and vertical reference lines used in Taekwon-Do.</figcaption>
                </figure>
            </>
        ),
    },
    {
        id: 'theory-of-power',
        title: 'Theory of Power (Hee-mui Wolli)',
        content: (
            <>
                <p>
                    The main scientific foundation upon which Taekwon-Do is built is called, in Taekwon-Do terms, the "Theory of Power". In the Taekwon-Do Encyclopaedia, General Choi lists six main principles that a student should know in order to achieve maximum power with their techniques. The first three are based on the laws of physics &ndash; Newton&rsquo;s Third Law of Motion, pressure, and kinetic energy. The second three are based on the human body and how best to achieve maximum effect.
                </p>
                <br />
                <h3 className="theory-subheading">Reaction Force</h3>
                <p>
                    This is about Newton&rsquo;s Third Law of Motion, from which is derived the law of Conservation of Momentum. Newton&rsquo;s Third Law states that every action has an equal and opposite reaction; the forces of two bodies on each other are always equal but acting in opposite directions.
                </p>
                <br />
                <p>
                    For example, when standing on the ground, your body exerts a force on the ground due to gravity. The ground pushes up with the exact same force &ndash; it must, or you would fall through.
                </p>
                <br />
                <p>
                    Another example: if two people on roller-skates push each other away, they will both roll apart. Before the push they had zero momentum; after the push they still have a net momentum of zero (equal momentum in opposite directions, cancelling out).
                </p>
                <br />
                <p>
                    A crash between two cars is worse if both are moving towards each other than if a moving car hits a stationary one. With one car stationary, momentum transfers from the moving car to the stationary car and some energy is carried away as the stationary car is pushed. In a head-on collision, however, the opposing directions cancel to a net momentum of zero, and all the energy goes into the crash and the resulting damage.
                </p>
                <br />
                <p>
                    Applied to Taekwon-Do, this means that punching an opponent who is moving towards you will be more effective than punching a stationary opponent.
                </p>
                <br />
                <h3 className="theory-subheading">Concentration</h3>
                <p>
                    Concentration relates to pressure &ndash; force divided by area. If we concentrate the same force into a smaller area, we achieve greater pressure.
                </p>
                <br />
                <p>
                    A knife is a good example. It is easier to cut with the sharp edge of the blade than with the blunt edge, because the sharp side has a much smaller surface area and so "concentrates" the force. When striking, we therefore use tools that concentrate force, such as the forefist, knifehand and footsword.
                </p>
                <br />
                <h3 className="theory-subheading">Mass and Speed</h3>
                <p>
                    This is the principle of Kinetic Energy. The word "kinetic" relates to motion, and all moving objects have kinetic energy. It is this kinetic energy that causes the damage in the car-crash example.
                </p>
                <p className="theory-formula">
                    KE = &frac12; mv&sup2;
                </p>
                <p>
                    Doubling an object&rsquo;s mass doubles its kinetic energy &ndash; a 2000&nbsp;kg car travelling at 30&nbsp;mph has twice the KE of a 1000&nbsp;kg car at 30&nbsp;mph.
                </p>
                <br />
                <p>
                    But the formula also shows that doubling the object&rsquo;s velocity <strong>quadruples</strong> its kinetic energy (2&sup2; = 4), and trebling it increases KE <strong>nine-fold</strong> (3&sup2; = 9). A 1000&nbsp;kg car at 30&nbsp;mph therefore has four times the KE of a 1000&nbsp;kg car at 15&nbsp;mph.
                </p>
                <br />
                <h3 className="theory-subheading">Breath Control</h3>
                <p>
                    This relates to stamina and muscle strength, both of which are results of dedicated training and fitness.
                </p>
                <br />
                <p>
                    When striking an opponent, a sharp exhalation of breath at the point of impact helps to tense the abdominal muscles. This steadies the body, generates more power in the technique, and readies the body for any counter-attack.
                </p>
                <br />
                <p>
                    Controlled breathing can also help to disguise outward signs of fatigue &ndash; an experienced opponent will press forward with an attack if they realise their opponent is tiring.
                </p>
                <br />
                <h3 className="theory-subheading">Equilibrium</h3>
                <p>
                    This relates to balance. Good balance and a lowered centre of gravity enable a more powerful blow to be delivered and help in reacting to any counter-attack that might come. It also enables more effective blocking and evasion. An unbalanced body is more easily toppled by an opponent.
                </p>
                <br />
                <h3 className="theory-subheading">Reflex</h3>
                <p>
                    This relates to speed. The faster a technique is delivered, the more power is generated (see Kinetic Energy above), and the less time an opponent has to react.
                </p>
                <br />
                <p>
                    The best way to generate speed is by keeping the muscles relaxed until the point of impact &ndash; tensed muscles react more slowly than relaxed ones.
                </p>
                <br />
                <figure className="theory-figure">
                    <div className="theory-video">
                        <iframe
                            src="https://www.youtube-nocookie.com/embed/pvpFvJUTCo8"
                            title="The Art of High Impact Kicking &mdash; Hwang Jang Lee"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    </div>
                    <figcaption>
                        Hwang Jang Lee (ITF 9th dan) demonstrating high-impact kicking &ndash; a practical illustration of the six principles above.
                    </figcaption>
                </figure>
                <p className="theory-footnote">
                    Many people are confused by the &frac12; in the kinetic-energy formula. It is merely a constant and can be applied at any point &ndash; one could multiply mass by velocity squared, then divide the whole result by two.
                </p>
            </>
        ),
    },
    {
        id: 'training-secrets',
        title: 'Training Secrets',
        content: (
            <>
                <p>
                    The traditional "Training Secrets of Taekwon-Do" guide the student toward efficient, powerful and well-coordinated technique:
                </p>
                <ol className="theory-list">
                    <li>Study the Theory of Power thoroughly.</li>
                    <li>Understand the purpose and method of each movement clearly.</li>
                    <li>Bring the movement of the eyes, hands, feet and breath into a single coordinated action.</li>
                    <li>Choose the appropriate attacking tool for each target.</li>
                    <li>Become familiar with the correct angle and distance for attack and defence.</li>
                    <li>Keep both the arms and legs bent slightly while the movement is in motion.</li>
                    <li>Begin all movements with a backward motion, with very few exceptions. Once the movement is in motion, however, it should not be stopped before reaching the target.</li>
                    <li>Create a sine wave during the movement, by utilising knee spring properly.</li>
                    <li>Exhale briefly at the moment of each blow, except during a Connecting Motion.</li>
                </ol>
            </>
        ),
    },
    {
        id: 'sine-wave',
        title: 'Sine Wave (Hwaldung Pado)',
        content: (
            <>
                <p>
                    Sine Wave was introduced to Taekwon-Do in 1983 by General Choi, and it is this element, arguably more than any other, that sets ITF Taekwon-Do apart from other martial arts.
                </p>
                <br />
                <p>
                    In mathematics, a sine wave is a function that describes a smooth, repetitive oscillation. One such oscillation is the "down-up-down" motion that the human body makes naturally when it walks, and it is that motion that is emphasised in Taekwon-Do: a smooth down-up-down movement incorporated into the transition between nearly all techniques. This exaggeration of the body&rsquo;s natural motion is one of the things that enables practitioners of Taekwon-Do to move quickly and to deliver powerful techniques.
                </p>
                <br />
                <p>
                    During training, the correct application of the sine wave teaches the student to move smoothly and with control, and adds stability to the movement by developing an understanding of how the body&rsquo;s centre of gravity affects the transition between techniques.
                </p>
            </>
        ),
    },
    {
        id: 'vital-spots',
        title: 'Vital Spots (Kupso)',
        content: (
            <>
                <p>
                    Vital Spots are areas in the body that are particularly vulnerable to attack and which, if attacked, can result in a disproportionately devastating outcome. This might be due to physical weakness (e.g. the eyes), reflexive response (e.g. the solar plexus), extreme pain (e.g. the groin), potential for temporary limb disability (e.g. the deltoid) or being part of the body&rsquo;s automatic nervous system (e.g. the windpipe).
                </p>
                <br />
                <p>
                    Not all vital spots are vulnerable in the same way. Some require a destructive technique such as a kick, whereas others will respond to a more focused attack, such as a thumb or knuckle; others will respond best to holds or joint locks. When you learn a new attacking technique, take time to learn which targets it is most effective against.
                </p>
                <br />
                <p>
                    A successful attack to a vital spot with an appropriate attacking tool is what gives the practitioner of Taekwon-Do the best chance of achieving victory in a single blow.
                </p>
            </>
        ),
    },
    {
        id: 'types-of-motion',
        title: 'Types of Motion',
        content: (
            <>
                <p>
                    Taekwon-Do techniques can be combined in several distinct ways, each with its own timing, breathing pattern and use of the sine wave.
                </p>
                <br />
                <h3 className="theory-subheading">Continuous Motion (Iojin Tongjak)</h3>
                <p>
                    Techniques performed one after the other with no pause between the end of one movement and the start of the next. There is one breath control for each technique and a sine wave for each move. Continuous Motion always starts with a block. Its purpose is to allow an instant response to an attack, either by blocking the next technique or issuing your own counter-attack.
                </p>
                <br />
                <h3 className="theory-subheading">Connecting Motion (Yon-gyol Tongjak)</h3>
                <p>
                    Techniques performed with no pause between them, only one breath, and with both techniques performed during the same sine wave. Connecting Motion is always performed with two techniques using opposite arms. One reason for Connecting Motion is to use one technique to "set up" an opponent for an immediate attack.
                </p>
                <br />
                <h3 className="theory-subheading">Consecutive Motion (Yonsok Tongjak)</h3>
                <p>
                    Two or more techniques are performed using the same limb without landing or stopping between them.
                </p>
                <br />
                <h3 className="theory-subheading">Fast Motion (Parun Tongjak)</h3>
                <p>
                    Techniques performed with a split-second pause between the end of one movement and the start of the next. The sine wave is performed as usual for the first movement, but the second movement only has a two-thirds sine wave. You breathe normally with one breath for each technique.
                </p>
                <br />
                <h3 className="theory-subheading">Slow Motion (Noo-rin Tongjak)</h3>
                <p>
                    Techniques performed slowly. The hands and feet move slowly and the breath is "drawn out". The reason for Slow Motion is to develop strength, technique, balance, breath control and timing.
                </p>
                <br />
                <h3 className="theory-subheading">Stamping Motion (Koo-ru-nun Tongjak)</h3>
                <p>
                    Performed when landing into the stance. The motion is created by landing forcefully with the leading leg and using the side sole to create the stamp. The reason for Stamping Motion is to accentuate the technique being performed.
                </p>
            </>
        ),
    },
    {
        id: 'ready-postures',
        title: 'Ready Postures',
        content: (
            <>
                <p>
                    Ready postures are the starting positions adopted before a pattern or exercise begins.
                </p>
                <br />
                <table className="theory-table">
                    <thead>
                        <tr><th>Ready Posture</th><th>Description</th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Parallel Ready Stance (Narani Junbi Sogi)</td>
                            <td>Feet shoulder-width apart, fists 5&nbsp;cm apart and 7&nbsp;cm in front of the abdomen.</td>
                        </tr>
                        <tr>
                            <td>Close Ready Stance A (Moa Junbi Sogi A)</td>
                            <td>Feet together, fists 30&nbsp;cm in front of the philtrum.</td>
                        </tr>
                        <tr>
                            <td>Close Ready Stance B (Moa Junbi Sogi B)</td>
                            <td>Feet together, fists 15&nbsp;cm in front of the navel.</td>
                        </tr>
                        <tr>
                            <td>Close Ready Stance C (Moa Junbi Sogi C)</td>
                            <td>Feet together, hands 10&nbsp;cm in front of the abdomen.</td>
                        </tr>
                        <tr>
                            <td>Close Ready Stance D (Moa Junbi Sogi D)</td>
                            <td>Feet together, hands 30&nbsp;cm away from the thighs.</td>
                        </tr>
                    </tbody>
                </table>
            </>
        ),
    },
    {
        id: 'one-step-sparring',
        title: 'One-Step Sparring (Ilbo Matsogi)',
        content: (
            <>
                <p>
                    One-step sparring is an advancement on the earlier step-sparring drills. Its purpose is to teach the importance of gaining "victory in one blow". It also teaches students to react to any attack, to block or evade it accordingly, and to counter it with the use of the correct tool, aimed at an appropriate "vital spot".
                </p>
                <br />
                <p>
                    Students will be expected to create their own sequences.
                </p>
            </>
        ),
    },
    {
        id: 'korean-terminology',
        title: 'Korean Terminology',
        content: (
            <>
                <p>
                    A reference glossary of common ITF Taekwon-Do terms, grouped by category. Tap a heading to expand it.
                </p>
                <br />

                <details className="theory-details">
                    <summary>Attacking &amp; Blocking Tools</summary>
                    <h4 className="theory-subheading-sm">Hand and Arm</h4>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Angle Fingertip</td><td>Homi Sonkut</td></tr>
                            <tr><td>Arc Hand</td><td>Bandalson</td></tr>
                            <tr><td>Back Hand</td><td>Sondung</td></tr>
                            <tr><td>Backfist</td><td>Dung Joomok</td></tr>
                            <tr><td>Base of Knifehand</td><td>Sonkal Patang</td></tr>
                            <tr><td>Bear Hand</td><td>Gomson</td></tr>
                            <tr><td>Bow Wrist</td><td>Sonmok Dung</td></tr>
                            <tr><td>Double Fingertip</td><td>Doo Songarak</td></tr>
                            <tr><td>Elbow</td><td>Palkup</td></tr>
                            <tr><td>Finger Belly</td><td>Songarak Padak</td></tr>
                            <tr><td>Finger Pincers</td><td>Chip-ge-son</td></tr>
                            <tr><td>Fingertip</td><td>Sonkut</td></tr>
                            <tr><td>Fore-knuckle Fist</td><td>Inji Joomok</td></tr>
                            <tr><td>Forefinger</td><td>Han Songarak</td></tr>
                            <tr><td>Forefist</td><td>Ap Joomok</td></tr>
                            <tr><td>Inner Forearm</td><td>An Palmok</td></tr>
                            <tr><td>Knifehand</td><td>Sonkal</td></tr>
                            <tr><td>Long Fist</td><td>Ghin Joomok</td></tr>
                            <tr><td>Middle-knuckle Fist</td><td>Joongji Joomok</td></tr>
                            <tr><td>Open Fist</td><td>Pyon Joomok</td></tr>
                            <tr><td>Outer Forearm</td><td>Bakat Palmok</td></tr>
                            <tr><td>Palm</td><td>Sonbadak</td></tr>
                            <tr><td>Press Finger</td><td>Chi-ap</td></tr>
                            <tr><td>Reverse Knifehand</td><td>Sonkal Dung</td></tr>
                            <tr><td>Sidefist</td><td>Yop Joomok</td></tr>
                            <tr><td>Thumb</td><td>Omji Songarak</td></tr>
                            <tr><td>Thumb Ridge</td><td>Omji Patang</td></tr>
                            <tr><td>Thumb-knuckle Fist</td><td>Omji Joomok</td></tr>
                            <tr><td>Under Fist</td><td>Mit Joomok</td></tr>
                            <tr><td>Under Forearm</td><td>Mit Palmok</td></tr>
                        </tbody>
                    </table>
                    <h4 className="theory-subheading-sm">Foot and Leg</h4>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Back Heel</td><td>Dwitchook</td></tr>
                            <tr><td>Back Sole</td><td>Dwit Kumchi</td></tr>
                            <tr><td>Ball of the Foot</td><td>Ap Kumchi</td></tr>
                            <tr><td>Footsword</td><td>Balkal</td></tr>
                            <tr><td>Instep</td><td>Baldung</td></tr>
                            <tr><td>Knee</td><td>Moorup</td></tr>
                            <tr><td>Reverse Footsword</td><td>Balkal Dung</td></tr>
                            <tr><td>Side Instep</td><td>Yop Baldung</td></tr>
                            <tr><td>Side Sole</td><td>Yop Bal Badak</td></tr>
                            <tr><td>Toes</td><td>Balkut</td></tr>
                        </tbody>
                    </table>
                    <h4 className="theory-subheading-sm">Miscellaneous Parts</h4>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Forehead</td><td>Ee-ma</td></tr>
                            <tr><td>Shoulder</td><td>O-kae</td></tr>
                            <tr><td>Outer Ankle Joint</td><td>Bakat Balmok Kwanjol</td></tr>
                            <tr><td>Inner Ankle Joint</td><td>An Bakat Kwanjol</td></tr>
                            <tr><td>Tibia</td><td>Kyong-gol</td></tr>
                            <tr><td>Inner Tibia</td><td>An Kyong-gol</td></tr>
                            <tr><td>Back Tibia</td><td>Dwit Kyong-gol</td></tr>
                            <tr><td>Shin</td><td>Chong-gang-i</td></tr>
                            <tr><td>Toe Edge</td><td>Palgarak Nal</td></tr>
                            <tr><td>Outer Tibia</td><td>Bakat Kyong-gol</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Blocks (Makgi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Checking Block</td><td>Mom-chwo Makgi</td></tr>
                            <tr><td>Circular Block</td><td>Dollimyo Makgi</td></tr>
                            <tr><td>Double Forearm Block</td><td>Doo Palmok Makgi</td></tr>
                            <tr><td>Downward Block</td><td>Naeryo Makgi</td></tr>
                            <tr><td>Front Block</td><td>Ap Makgi</td></tr>
                            <tr><td>Grasping Block</td><td>Putjaba Makgi</td></tr>
                            <tr><td>High Block</td><td>Daebi Makgi</td></tr>
                            <tr><td>Hooking Block</td><td>Nopundae Makgi</td></tr>
                            <tr><td>Inside Block</td><td>Golcho Makgi</td></tr>
                            <tr><td>Inward Block</td><td>An Makgi</td></tr>
                            <tr><td>Low Block</td><td>Najundae Makgi</td></tr>
                            <tr><td>Luring Block</td><td>Yu-in Makgi</td></tr>
                            <tr><td>Middle Block</td><td>Ka-un-dae Makgi</td></tr>
                            <tr><td>Nine-shape Block</td><td>Gutja Makgi</td></tr>
                            <tr><td>Outside Block</td><td>Bakat Makgi</td></tr>
                            <tr><td>Outward Block</td><td>Bakuro Makgi</td></tr>
                            <tr><td>Parallel Block</td><td>Narani Makgi</td></tr>
                            <tr><td>Pressing Block</td><td>Noollo Makgi</td></tr>
                            <tr><td>Pushing Block</td><td>Miro Makgi</td></tr>
                            <tr><td>Rising Block</td><td>Chookyo Makgi</td></tr>
                            <tr><td>Scooping Block</td><td>Duro Makgi</td></tr>
                            <tr><td>Side Front Block</td><td>Yop Ap Makgi</td></tr>
                            <tr><td>Twin Forearm Block</td><td>Sang Palmok Makgi</td></tr>
                            <tr><td>Upward Block</td><td>Ollyo Makgi</td></tr>
                            <tr><td>U-shape Block</td><td>Digutja Makgi</td></tr>
                            <tr><td>U-shape Grasp</td><td>Digutja Japgi</td></tr>
                            <tr><td>W-shape Block</td><td>San Makgi</td></tr>
                            <tr><td>Waist Block</td><td>Hori Makgi</td></tr>
                            <tr><td>Wedging Block</td><td>Hechyo Makgi</td></tr>
                            <tr><td>X Fist Block</td><td>Kyocha Joomok Makgi</td></tr>
                            <tr><td>X Knifehand Block</td><td>Kyocha Sonkal Makgi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Kicks &ndash; Attacking (Chagi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Back Kick</td><td>Dwit Chagi</td></tr>
                            <tr><td>Back Piercing Kick</td><td>Dwit Cha Jirugi</td></tr>
                            <tr><td>Combination Kick</td><td>Honhap Chagi</td></tr>
                            <tr><td>Consecutive Kick</td><td>Yonsok Chagi</td></tr>
                            <tr><td>Downward Kick</td><td>Naeryo Chagi</td></tr>
                            <tr><td>Flying Kick</td><td>Twimyo Chagi</td></tr>
                            <tr><td>Flying High Kick</td><td>Twimyo Nopi Chagi</td></tr>
                            <tr><td>Flying Spiral Kick</td><td>Twimyo Rason-shik Chagi</td></tr>
                            <tr><td>Flying Twin-Foot Front Kick</td><td>Twimyo Sangbal Ap Chagi</td></tr>
                            <tr><td>Front Kick</td><td>Ap Chagi</td></tr>
                            <tr><td>Front-leg Kick</td><td>Ap Tari Chagi</td></tr>
                            <tr><td>Front Pushing Kick</td><td>Ap Cha Milgi</td></tr>
                            <tr><td>Front Snap Kick</td><td>Ap Cha Busigi</td></tr>
                            <tr><td>High Kick</td><td>Nopi Chagi</td></tr>
                            <tr><td>Inward Kick</td><td>Anuro Chagi</td></tr>
                            <tr><td>Inward Vertical Kick</td><td>Anuro Sewo Chagi</td></tr>
                            <tr><td>Mid-air (jump rotating) Kick</td><td>Twimyo Dolymo Chagi</td></tr>
                            <tr><td>Outward Kick</td><td>Bakuro Chagi</td></tr>
                            <tr><td>Outward Vertical Kick</td><td>Bakuro Sewo Chagi</td></tr>
                            <tr><td>Pick-Shape Kick</td><td>Gok-gwaeng-i Chagi</td></tr>
                            <tr><td>Pressing Kick</td><td>Noollo Chagi</td></tr>
                            <tr><td>Reflex Kick</td><td>Pansa Chagi</td></tr>
                            <tr><td>Reverse Turning Kick</td><td>Bandae Dollyo Chagi</td></tr>
                            <tr><td>Reverse Hook Kick</td><td>Bandae Dollyo Goro Chagi</td></tr>
                            <tr><td>Side Front Kick</td><td>Yop Ap Chagi</td></tr>
                            <tr><td>Side Front Snap Kick</td><td>Yop Ap-Cha Busigi</td></tr>
                            <tr><td>Side Piercing Kick</td><td>Yop Cha Jirugi</td></tr>
                            <tr><td>Side Pushing Kick</td><td>Yop Cha Milgi</td></tr>
                            <tr><td>Side Thrusting Kick</td><td>Yop Cha Tulgi</td></tr>
                            <tr><td>Sweeping Kick</td><td>Suro Chagi</td></tr>
                            <tr><td>Turning Kick</td><td>Dollyo Chagi</td></tr>
                            <tr><td>Twisting Kick</td><td>Bituro Chagi</td></tr>
                            <tr><td>Two Direction Kick</td><td>Sangbang Chagi</td></tr>
                            <tr><td>Vertical Kick</td><td>Sewo Chagi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Kicks &ndash; Blocking</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Crescent Kick</td><td>Bandal Chagi</td></tr>
                            <tr><td>Front Rising Kick</td><td>Ap Cha Olligi</td></tr>
                            <tr><td>Front Checking Kick</td><td>Ap Cha Momchugi</td></tr>
                            <tr><td>Hooking Kick</td><td>Golcho Chagi</td></tr>
                            <tr><td>Side Rising Kick</td><td>Yop Cha Olligi</td></tr>
                            <tr><td>Side Checking Kick</td><td>Yop Cha Momchugi</td></tr>
                            <tr><td>Waving Kick</td><td>Doro Chagi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Punches (Jirugi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Angle Punch</td><td>Giyokja Jirugi</td></tr>
                            <tr><td>Crescent Punch</td><td>Bandal Jirugi</td></tr>
                            <tr><td>Dodging Punch</td><td>Pihamyo Jirugi</td></tr>
                            <tr><td>Downward Punch</td><td>Naeryo Jirugi</td></tr>
                            <tr><td>Flying Consecutive Punch</td><td>Twimyo Yonsok Jirugi</td></tr>
                            <tr><td>Flying Punch</td><td>Twimyo Jirugi</td></tr>
                            <tr><td>Flying Overhead Punch</td><td>Twiyo Nomo Jirugi</td></tr>
                            <tr><td>Front Punch</td><td>Ap Jirugi</td></tr>
                            <tr><td>High Punch</td><td>Nopundae Jirugi</td></tr>
                            <tr><td>Horizontal Punch</td><td>Su-pyong Jirugi</td></tr>
                            <tr><td>Knuckle-Fist Punch</td><td>Songarak Joomok Jirugi</td></tr>
                            <tr><td>Long-Fist Punch</td><td>Ghin Joomok Jirugi</td></tr>
                            <tr><td>Low Punch</td><td>Najundae Jirugi</td></tr>
                            <tr><td>Mid-Air Punch (jump rotating)</td><td>Twiyo Dolymo Jirugi</td></tr>
                            <tr><td>Middle Punch</td><td>Ka-un-dae Jirugi</td></tr>
                            <tr><td>Obverse Punch</td><td>Baro Jirugi</td></tr>
                            <tr><td>Open-Fist Punch</td><td>Pyon Joomok Jirugi</td></tr>
                            <tr><td>Reverse Punch</td><td>Bandae Jirugi</td></tr>
                            <tr><td>Side Front Punch</td><td>Yop Ap Jirugi</td></tr>
                            <tr><td>Side Punch</td><td>Yop Jirugi</td></tr>
                            <tr><td>Side Vertical Punch</td><td>Yop Sewo Jirugi</td></tr>
                            <tr><td>Turning Punch</td><td>Dollyo Jirugi</td></tr>
                            <tr><td>Upset Punch</td><td>Dwijibo Jirugi</td></tr>
                            <tr><td>Upward Punch</td><td>Ollyo Jirugi</td></tr>
                            <tr><td>U-Shape Punch</td><td>Digutja Jirugi</td></tr>
                            <tr><td>Vertical Punch</td><td>Sewo Jirugi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Stances (Sogi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Walking Stance</td><td>Gunnun Sogi</td></tr>
                            <tr><td>Sitting Stance</td><td>Annun Sogi</td></tr>
                            <tr><td>L Stance</td><td>Niunja Sogi</td></tr>
                            <tr><td>X Stance</td><td>Kyocha Sogi</td></tr>
                            <tr><td>Closed Stance</td><td>Moa Sogi</td></tr>
                            <tr><td>Parallel Stance</td><td>Narani Sogi</td></tr>
                            <tr><td>Rear Foot Stance</td><td>Dwitbal Sogi</td></tr>
                            <tr><td>Vertical Stance</td><td>Soojik Sogi</td></tr>
                            <tr><td>Low Stance</td><td>Nat-chwo Sogi</td></tr>
                            <tr><td>Fixed Stance</td><td>Gojung Sogi</td></tr>
                            <tr><td>Bending Ready Stance</td><td>Guburyo Junbi Sogi</td></tr>
                            <tr><td>Attention Stance</td><td>Charyot Sogi</td></tr>
                            <tr><td>Diagonal Stance</td><td>Sason Sogi</td></tr>
                            <tr><td>One-Leg Stance</td><td>Waebal Sogi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Strikes (Taerigi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Back Elbow Strike</td><td>Dwit Palkup Taerigi</td></tr>
                            <tr><td>Backfist Strike</td><td>Dung Joomok Taerigi</td></tr>
                            <tr><td>Back Strike</td><td>Dwit Taerigi</td></tr>
                            <tr><td>Crescent Strike</td><td>Bandal Taerigi</td></tr>
                            <tr><td>Dodging Strike</td><td>Pihamyo Taerigi</td></tr>
                            <tr><td>Double Strike</td><td>I-jung Taerigi</td></tr>
                            <tr><td>Downward Strike</td><td>Naeryo Taerigi</td></tr>
                            <tr><td>Elbow Strike</td><td>Palkup Taerigi</td></tr>
                            <tr><td>Flying Strike</td><td>Twimyo Taerigi</td></tr>
                            <tr><td>Front Downward Strike</td><td>Ap Naeryo Taerigi</td></tr>
                            <tr><td>Front Strike</td><td>Ap Taerigi</td></tr>
                            <tr><td>High Strike</td><td>Nopundae Taerigi</td></tr>
                            <tr><td>Horizontal Strike</td><td>Su-pyong Taerigi</td></tr>
                            <tr><td>Inward Strike</td><td>Anuro Taerigi</td></tr>
                            <tr><td>Knifehand Strike</td><td>Sonkal Taerigi</td></tr>
                            <tr><td>Low Strike</td><td>Najundae Taerigi</td></tr>
                            <tr><td>Mid-Air Strike</td><td>Twiyo Dolymo Taerigi</td></tr>
                            <tr><td>Middle Strike</td><td>Ka-un-dae Taerigi</td></tr>
                            <tr><td>Outward Strike</td><td>Bakuro Taerigi</td></tr>
                            <tr><td>Side Back Strike</td><td>Yop Dwit Taerigi</td></tr>
                            <tr><td>Side Downward Strike</td><td>Yop Naeryo Taerigi</td></tr>
                            <tr><td>Side Front Strike</td><td>Yop Ap Taerigi</td></tr>
                            <tr><td>Side Strike</td><td>Yop Taerigi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Thrusts (Tulgi)</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Back Elbow Thrust</td><td>Dwit Palkup Tulgi</td></tr>
                            <tr><td>Downward Thrust</td><td>Naeryo Tulgi</td></tr>
                            <tr><td>Flying Thrust</td><td>Twimyo Tulgi</td></tr>
                            <tr><td>High Thrust</td><td>Nopundae Tulgi</td></tr>
                            <tr><td>Horizontal Thrust</td><td>Su-pyong Tulgi</td></tr>
                            <tr><td>Low Thrust</td><td>Najundae Tulgi</td></tr>
                            <tr><td>Mid-Air Thrust</td><td>Twiyo Dolymo Tulgi</td></tr>
                            <tr><td>Middle Thrust</td><td>Ka-un-dae Tulgi</td></tr>
                            <tr><td>Side Back Thrust</td><td>Yop Dwit Tulgi</td></tr>
                            <tr><td>Side Front Thrust</td><td>Yop Ap Tulgi</td></tr>
                            <tr><td>Side Thrust</td><td>Yop Tulgi</td></tr>
                        </tbody>
                    </table>
                </details>

                <details className="theory-details">
                    <summary>Other Terminology</summary>
                    <table className="theory-table">
                        <thead><tr><th>English</th><th>Korean</th></tr></thead>
                        <tbody>
                            <tr><td>Belt</td><td>Ti</td></tr>
                            <tr><td>Breath Control</td><td>Hohup Jojol</td></tr>
                            <tr><td>Dodging</td><td>Pihagi</td></tr>
                            <tr><td>Double Stepping</td><td>Ibo Omgyo Didigi</td></tr>
                            <tr><td>Foot Shifting</td><td>Jajunbal</td></tr>
                            <tr><td>Free Sparring</td><td>Jayoo Matsogi</td></tr>
                            <tr><td>Fundamental Exercise</td><td>Gibon Yonsup</td></tr>
                            <tr><td>Obverse</td><td>Baro</td></tr>
                            <tr><td>One-Step Sparring</td><td>Ilbo Matsogi</td></tr>
                            <tr><td>Pattern</td><td>Tul</td></tr>
                            <tr><td>Release from Grab</td><td>Jappyosul Tae</td></tr>
                            <tr><td>Reverse</td><td>Bandae</td></tr>
                            <tr><td>Self Defence</td><td>Hosinsul</td></tr>
                            <tr><td>Semi-Free Sparring</td><td>Ban Jayoo Matsogi</td></tr>
                            <tr><td>Sine Wave</td><td>Hwaldung Pado</td></tr>
                            <tr><td>Sliding</td><td>Mikulgi</td></tr>
                            <tr><td>Sparring</td><td>Matsogi</td></tr>
                            <tr><td>Special Technique</td><td>Tukgi</td></tr>
                            <tr><td>Spot Turning</td><td>Gujari Dolgi</td></tr>
                            <tr><td>Step Turning</td><td>Omgyo Didimyo Dolgi</td></tr>
                            <tr><td>Stepping</td><td>Omgyo Didigi</td></tr>
                            <tr><td>Three-Step Sparring</td><td>Sambo Matsogi</td></tr>
                            <tr><td>Training Suit</td><td>Dobok</td></tr>
                            <tr><td>Turning</td><td>Dolgi</td></tr>
                            <tr><td>Two-Step Sparring</td><td>Ibo Matsogi</td></tr>
                            <tr><td>Vital Spots</td><td>Kupso</td></tr>
                        </tbody>
                    </table>
                </details>
            </>
        ),
    },
    {
        id: 'competition-sparring-rules',
        title: 'Competition Sparring Rules',
        content: (
            <>
                <p>
                    When competing in a Taekwon-Do competition, it is important to understand the rules. The following is a brief description of the ITF tournament rules that are applied to colour belts and black belts alike, at all levels of competition, from local competitions to the World Championships.
                </p>
                <br />
                <h3 className="theory-subheading">Point Scoring</h3>
                <p>
                    ITF free sparring is continuous point scoring &ndash; points are scored during continuous fighting. To score a point you must direct a valid Taekwon-Do attacking technique at a legal target area on your opponent and be within 2cm of making contact.
                </p>
                <br />
                <p>
                    ITF free-sparring is <strong>semi-contact</strong>, which means that only light contact is usually permitted. Heavy contact is not. For young children, only touch contact is allowed, with many competitions not allowing under 13&rsquo;s to punch to the face for safety reasons. For adult black belts, heavier contact may sometimes be permitted.
                </p>
                <br />
                <p>The legal target area is:</p>
                <ul className="theory-list">
                    <li><strong>Middle Section</strong> &ndash; torso from waist to neck, at the front and sides only (not the back)</li>
                    <li><strong>High Section</strong> &ndash; head at the front and sides (not the back)</li>
                </ul>
                <br />
                <p>
                    Sparring bouts are judged by four corner judges. Points awarded for scoring techniques are as follows:
                </p>
                <table className="theory-table">
                    <thead>
                        <tr>
                            <th>Points</th>
                            <th>Technique</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>Punch to the middle or high section (including jumping punch)</td>
                        </tr>
                        <tr>
                            <td>2</td>
                            <td>Kick to the middle section (including jumping kick)</td>
                        </tr>
                        <tr>
                            <td>3</td>
                            <td>Kick to the high section (including jumping kick)</td>
                        </tr>
                    </tbody>
                </table>
                <br />
                <p>
                    The winner is the competitor adjudged to have scored the most points, after deductions (see Warnings and Fouls below), within the time limit.
                </p>
                <br />
                <h3 className="theory-subheading">Warnings and Fouls</h3>
                <p>
                    When the centre referee sees an infringement, they will temporarily stop the bout and give a warning. Warnings are given for (most common shown first):
                </p>
                <ul className="theory-list">
                    <li><strong>Attack to an illegal target</strong> (below the belt or to the back)</li>
                    <li><strong>Stepping out of the ring</strong> (both feet)</li>
                    <li><strong>Falling down</strong> (any part of the body other than the feet touching the floor)</li>
                    <li>Intentionally avoiding sparring (turning your back on your opponent)</li>
                    <li>Holding, grabbing or pushing</li>
                    <li>Faking a blow (pretending to be injured)</li>
                    <li>Leg sweeping</li>
                    <li>Pretending to have scored a point (trying to influence the judges)</li>
                </ul>
                <br />
                <p>
                    One point is deducted from a competitor&rsquo;s score for every <strong>three</strong> warnings received. There is no limit to the number of warnings that a competitor can receive in a bout.
                </p>
                <br />
                <p>
                    When the referee sees a serious infringement, they will temporarily stop the bout and give a foul. Fouls are more serious than warnings and are given for:
                </p>
                <ul className="theory-list">
                    <li><strong>Heavy contact</strong></li>
                    <li><strong>Heavy contact to an illegal target</strong></li>
                    <li>Loss of temper</li>
                    <li>Insulting an opponent</li>
                    <li>Biting or scratching</li>
                    <li>Attacking a fallen opponent</li>
                    <li>Attacking with the knee, elbow or head</li>
                </ul>
                <br />
                <p>
                    A foul results in a one-point deduction. The accumulation of <strong>three fouls</strong> in a single bout results in the competitor being disqualified.
                </p>
                <br />
                <h3 className="theory-subheading">Further Information</h3>
                <p>
                    ITF competition also includes Patterns, Power Test, Special Technique and Pre-Arranged Sparring. These rules are far less complex than free sparring and are not covered here. For further information, speak to your Instructor and always refer to the rules of any competition at the time.
                </p>
            </>
        ),
    },
];
